import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { ghs, money, num, formatDate, todayISODate } from "../lib/format";
import { useCountry, countryInfo } from "../context/CountryContext";
import { Plus, MagnifyingGlass, X, ArrowRight, Copy, ArrowUDownLeft } from "@phosphor-icons/react";

const STATUS_CHIPS = {
  draft:      "bg-slate-100 text-slate-700 border-slate-200",
  ready:      "bg-sky-50 text-ps-blue border-sky-200",
  dispatched: "bg-orange-50 text-ps-orange border-orange-200",
  delivered:  "bg-lime-50 text-lime-700 border-lime-200",
  filed:      "bg-indigo-50 text-indigo-700 border-indigo-200",
  cancelled:  "bg-red-50 text-ps-red border-red-200",
};

const STATUS_FILTERS = ["", "draft", "ready", "dispatched", "delivered", "filed", "cancelled"];

const DEFAULT_FORM = (country = "GH") => ({
  order_date: todayISODate(),
  sender: { name: "", address: "", contact: "" },
  consignee: { name: "", address: "", contact: "" },
  origin: "",
  destination: "",
  cargo_description: "",
  packages: "",
  packing: "",
  weight_kg: "",
  volume_m3: "",
  hs_code: "",
  special_instructions: "",
  payment_terms: "Sender",
  agreed_charge_ghs: "",
  fuel_needed_l: "",
  vehicle_number: "",
  driver_name: "",
  notes: "",
  country,
  waybill_source: "portside",
  client_waybill_number: "",
  client_waybill_issuer: "",
});

export default function Trips() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { scope, filterParam, defaultCountry, isAll } = useCountry();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", q: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => DEFAULT_FORM(defaultCountry));
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [copiedFromCmr, setCopiedFromCmr] = useState("");
  const [customers, setCustomers] = useState([]);

  // Load address book once the dialog opens (lazy)
  useEffect(() => {
    if (open && customers.length === 0) {
      api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
    }
    // eslint-disable-next-line
  }, [open]);

  const pickCustomerAs = (role, customer) => {
    if (!customer) return;
    // Build a party-like object from the customer record
    const party = {
      name: customer.name,
      address: customer.address || "",
      contact: [customer.contact_name, customer.contact_phone, customer.contact_email].filter(Boolean).join(" · "),
    };
    setForm((f) => {
      const next = { ...f, [role]: party };
      // Sender controls waybill defaults
      if (role === "sender") {
        const src = customer.default_waybill_source || "portside";
        next.waybill_source = src;
        next.client_waybill_issuer = customer.default_client_waybill_issuer || (src === "client" ? customer.name : "");
      }
      return next;
    });
  };

  // Map a trip record into the form shape, dropping per-instance fields
  const fillFromTrip = (trip) => {
    setForm({
      order_date: todayISODate(),                 // always today
      sender: { ...(trip.sender || { name: "", address: "", contact: "" }) },
      consignee: { ...(trip.consignee || { name: "", address: "", contact: "" }) },
      origin: trip.origin || "",
      destination: trip.destination || "",
      cargo_description: trip.cargo_description || "",
      packages: trip.packages || "",
      packing: trip.packing || "",
      weight_kg: trip.weight_kg || "",
      volume_m3: trip.volume_m3 || "",
      hs_code: trip.hs_code || "",
      special_instructions: trip.special_instructions || "",
      payment_terms: trip.payment_terms || "Sender",
      agreed_charge_ghs: trip.agreed_charge_ghs || "",
      fuel_needed_l: trip.fuel_needed_l || "",
      vehicle_number: trip.vehicle_number || "",
      driver_name: trip.driver_name || "",
      notes: "",                                   // start clean
      country: trip.country || defaultCountry,
      waybill_source: trip.waybill_source || "portside",
      client_waybill_number: "",                   // a duplicated client waybill must get a fresh number
      client_waybill_issuer: trip.client_waybill_issuer || "",
    });
    setCopiedFromCmr(trip.cmr_number);
  };

  const copyLast = () => {
    if (rows.length === 0) return;
    fillFromTrip(rows[0]);
  };

  const duplicateRow = (e, trip) => {
    e.stopPropagation();
    fillFromTrip(trip);
    setOpen(true);
  };

  const load = async () => {
    setLoading(true);
    const params = {};
    if (filter.status) params.status = filter.status;
    if (filter.q) params.q = filter.q;
    if (filterParam) params.country = filterParam;
    const { data } = await api.get("/trips", { params });
    setRows(data);
    setLoading(false);
    return data;
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.status, filterParam]);

  // Keep form's country in sync with scope while it's clean (not editing/copied)
  useEffect(() => {
    if (!open && !copiedFromCmr) {
      setForm((f) => ({ ...f, country: defaultCountry }));
    }
    // eslint-disable-next-line
  }, [defaultCountry]);

  // Handle ?from=<id> coming from TripDetail's "Duplicate" button
  useEffect(() => {
    const fromId = searchParams.get("from");
    if (!fromId) return;
    api.get(`/trips/${fromId}`).then(({ data }) => {
      fillFromTrip(data);
      setOpen(true);
      setSearchParams({}, { replace: true });
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  // Handle ?from_activity=<id> — coming from the Trips/Activities page's "📄 Waybill" button.
  // We hydrate the CMR form with the activity's truck/driver/client so the operator only has to
  // fill in CMR-specific fields (sender/consignee address, cargo description, weight, etc.).
  useEffect(() => {
    const actId = searchParams.get("from_activity");
    if (!actId) return;
    Promise.all([
      api.get(`/activities/${actId}`).catch(() => null),
      api.get(`/customers`).catch(() => ({ data: [] })),
    ]).then(([actRes, custRes]) => {
      if (!actRes) return;
      const a = actRes.data;
      const allCustomers = custRes.data || [];
      setCustomers(allCustomers);
      const sender = allCustomers.find((c) => c.name === a.client);
      setForm((f) => ({
        ...f,
        order_date: (a.date || todayISODate()).slice(0, 10),
        sender: sender
          ? { name: sender.name, address: sender.address || "", contact: [sender.contact_name, sender.contact_phone, sender.contact_email].filter(Boolean).join(" · ") }
          : { name: a.client || "", address: "", contact: "" },
        consignee: { name: "", address: "", contact: "" },
        origin: a.origin || (sender?.address || ""),
        destination: a.destination || "",
        cargo_description: a.activity_detail || a.cargo_description || "",
        packages: a.box_count || "",
        weight_kg: a.tonnes_actual ? Math.round(a.tonnes_actual * 1000) : (a.tonnes_estimated ? Math.round(a.tonnes_estimated * 1000) : ""),
        fuel_needed_l: a.fuel_issued_l || a.fuel_approved_l || a.fuel_requested_l || "",
        vehicle_number: a.vehicle_number || "",
        driver_name: a.driver_name || "",
        notes: `Linked to activity ${a.id.slice(0, 8)} · ${a.date?.slice(0, 10) || ""}`,
        country: a.country || defaultCountry,
        waybill_source: sender?.default_waybill_source || "portside",
        client_waybill_issuer: sender?.default_client_waybill_issuer || "",
      }));
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("from_activity");
      setSearchParams(next, { replace: true });
    });
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      const payload = {
        order_date: new Date(form.order_date).toISOString(),
        sender: form.sender,
        consignee: form.consignee,
        origin: form.origin,
        destination: form.destination,
        cargo_description: form.cargo_description,
        packages: form.packages ? parseInt(form.packages) : 0,
        packing: form.packing,
        weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : 0,
        volume_m3: form.volume_m3 ? parseFloat(form.volume_m3) : 0,
        hs_code: form.hs_code,
        special_instructions: form.special_instructions,
        payment_terms: form.payment_terms,
        agreed_charge_ghs: form.agreed_charge_ghs ? parseFloat(form.agreed_charge_ghs) : 0,
        fuel_needed_l: form.fuel_needed_l ? parseFloat(form.fuel_needed_l) : 0,
        vehicle_number: form.vehicle_number,
        driver_name: form.driver_name,
        notes: form.notes,
        country: form.country || defaultCountry,
        waybill_source: form.waybill_source || "portside",
        client_waybill_number: (form.client_waybill_number || "").trim(),
        client_waybill_issuer: (form.client_waybill_issuer || "").trim(),
      };
      const { data } = await api.post("/trips", payload);
      setOpen(false);
      setForm(DEFAULT_FORM(defaultCountry));
      setCopiedFromCmr("");
      nav(`/trips/${data.id}`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const counts = STATUS_FILTERS.slice(1).reduce((acc, s) => {
    acc[s] = rows.filter((r) => r.status === s).length;
    return acc;
  }, {});
  // Totals split per currency since GH=GHS and TG=XOF cannot be summed
  const chargesByCur = rows.reduce((acc, r) => {
    const c = r.country === "TG" ? "XOF" : "GHS";
    acc[c] = (acc[c] || 0) + (r.agreed_charge_ghs || 0);
    return acc;
  }, {});

  return (
    <div data-testid="trips-page">
      <PageHeader
        subtitle={isAll ? "Order & waybill workflow · All countries" : `Order & waybill workflow · ${countryInfo(scope).flag} ${countryInfo(scope).name}`}
        title="Trips"
        action={
          <div className="flex gap-2">
            {rows.length > 0 && (
              <button
                onClick={() => { copyLast(); setOpen(true); }}
                data-testid="copy-last-button"
                className="border border-slate-300 bg-white text-ps-deep px-3 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-slate-50 transition-colors text-sm"
                title={`Copy from ${rows[0].cmr_number}`}
              >
                <Copy size={16} weight="bold" /> Copy from last
              </button>
            )}
            <button
              onClick={() => setOpen(true)}
              data-testid="new-trip-button"
              className="bg-ps-blue text-white px-4 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-ps-deep transition-colors shadow-sm"
            >
              <Plus size={16} weight="bold" /> New order
            </button>
          </div>
        }
      />

      {/* Status filter pills */}
      <div className="px-8 py-4 bg-slate-100 border-b border-slate-200 flex flex-wrap gap-2 items-center">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilter({ ...filter, status: s })}
            data-testid={`status-filter-${s || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
              filter.status === s ? "bg-ps-deep text-white" : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"
            }`}
          >
            {s || "All"} {s && counts[s] ? <span className="opacity-70 ml-1">{counts[s]}</span> : ""}
          </button>
        ))}
        <div className="relative ml-2 flex-1 max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={filter.q}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="Search CMR, client, truck…"
            data-testid="trips-search"
            className="w-full border border-slate-300 rounded-md bg-white pl-8 pr-3 py-2 text-sm"
          />
        </div>
        <button onClick={load} className="text-xs uppercase tracking-wider font-bold text-ps-blue hover:text-ps-deep">Apply</button>
        <div className="ml-auto flex gap-6 items-end flex-wrap">
          <div><div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Orders</div><div className="font-mono-data tabular text-xl text-ps-deep">{num(rows.length)}</div></div>
          {Object.keys(chargesByCur).length === 0 ? (
            <div><div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Agreed charges</div><div className="font-mono-data tabular text-xl text-lime-700">—</div></div>
          ) : Object.entries(chargesByCur).map(([cur, total]) => (
            <div key={cur} data-testid={`charges-${cur}`}>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 flex items-center gap-1">
                Agreed charges <span className="px-1.5 py-0.5 rounded-sm bg-slate-200 text-slate-700 font-bold text-[9px]">{cur}</span>
              </div>
              <div className="font-mono-data tabular text-xl text-lime-700">{money(total, cur)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 text-left">CMR №</th>
                {isAll && <th className="px-3 py-3 text-left w-10"></th>}
                <th className="px-3 py-3 text-left">Status</th>
                <th className="px-3 py-3 text-left">Order date</th>
                <th className="px-3 py-3 text-left">Sender</th>
                <th className="px-3 py-3 text-left">Consignee</th>
                <th className="px-3 py-3 text-left">Route</th>
                <th className="px-3 py-3 text-left">Truck</th>
                <th className="px-3 py-3 text-right">Charge</th>
                <th className="px-3 py-3 text-right">Fuel</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="trips-table-body">
              {loading && <tr><td colSpan={isAll ? 11 : 10} className="p-6"><div className="brand-progress w-1/3" /></td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={isAll ? 11 : 10} className="p-12 text-center text-slate-500">No orders. Click <span className="font-semibold text-ps-deep">New order</span> to start.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} onClick={() => nav(`/trips/${r.id}`)} data-testid={`trip-row-${r.id}`} className="hover:bg-slate-50 cursor-pointer">
                  <td className="px-3 py-2 font-mono-data text-ps-blue font-semibold">
                    {r.waybill_source === "client" ? (
                      <div>
                        <div className="text-ps-deep" title="Client waybill №">{r.client_waybill_number || "—"}</div>
                        <div className="text-[10px] text-slate-400 font-normal mt-0.5">int. {r.cmr_number}</div>
                      </div>
                    ) : (
                      r.cmr_number
                    )}
                  </td>
                  {isAll && <td className="px-3 py-2 text-sm" title={countryInfo(r.country).name}>{countryInfo(r.country).flag}</td>}
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${STATUS_CHIPS[r.status] || STATUS_CHIPS.draft}`}>{r.status}</span>
                  </td>
                  <td className="px-3 py-2 font-mono-data text-xs whitespace-nowrap">{formatDate(r.order_date)}</td>
                  <td className="px-3 py-2 truncate max-w-[160px]" title={r.sender?.name}>{r.sender?.name || "—"}</td>
                  <td className="px-3 py-2 truncate max-w-[160px] font-semibold text-ps-deep" title={r.consignee?.name}>{r.consignee?.name || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 truncate max-w-[180px]">{r.origin} <ArrowRight size={10} className="inline mx-1" /> {r.destination}</td>
                  <td className="px-3 py-2 font-mono-data text-xs text-ps-blue">{r.vehicle_number || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular text-lime-700 font-semibold">{r.agreed_charge_ghs ? money(r.agreed_charge_ghs, r.country === "TG" ? "XOF" : "GHS") : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular">
                    {r.fuel_litres
                      ? <span className="text-ps-blue">{num(r.fuel_litres)} L</span>
                      : r.fuel_needed_l
                        ? <span className="text-amber-700" title="Planned (not yet allocated)">{num(r.fuel_needed_l)} L *</span>
                        : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={(e) => duplicateRow(e, r)}
                        data-testid={`duplicate-trip-${r.id}`}
                        title="Duplicate this order"
                        className="text-slate-400 hover:text-ps-blue p-1 rounded hover:bg-sky-50 transition-colors"
                      >
                        <Copy size={14} weight="bold" />
                      </button>
                      <ArrowRight size={14} weight="bold" className="text-slate-400" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* New order slide-over */}
      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex justify-end" onClick={() => setOpen(false)} data-testid="trip-dialog">
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white h-full overflow-y-auto border-l border-slate-200">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">New order</div>
                <h2 className="font-display font-extrabold text-2xl tracking-tight text-ps-deep">Open a CMR waybill</h2>
              </div>
              <button type="button" onClick={() => { setOpen(false); setCopiedFromCmr(""); }} className="text-slate-500"><X size={20} /></button>
            </div>

            {/* Copy-from banner / shortcut */}
            <div className="px-6 pt-4">
              {copiedFromCmr ? (
                <div className="flex items-center justify-between bg-sky-50 border border-sky-200 rounded-md px-3 py-2 text-sm" data-testid="copied-from-banner">
                  <div className="flex items-center gap-2 text-ps-deep">
                    <Copy size={14} weight="fill" className="text-ps-blue" />
                    <span>Copied from <span className="font-mono-data font-semibold">{copiedFromCmr}</span> &mdash; review &amp; edit before saving.</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setForm(DEFAULT_FORM(defaultCountry)); setCopiedFromCmr(""); }}
                    data-testid="clear-copy"
                    className="text-xs uppercase tracking-wider font-bold text-ps-blue hover:text-ps-deep flex items-center gap-1"
                  >
                    <ArrowUDownLeft size={12} weight="bold" /> Clear
                  </button>
                </div>
              ) : rows.length > 0 ? (
                <button
                  type="button"
                  onClick={copyLast}
                  data-testid="dialog-copy-last"
                  className="w-full border border-dashed border-slate-300 rounded-md px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:border-ps-blue hover:text-ps-deep transition-colors flex items-center justify-center gap-2"
                >
                  <Copy size={14} weight="bold" />
                  Copy from last order — <span className="font-mono-data text-ps-blue font-semibold">{rows[0].cmr_number}</span>
                  <span className="text-xs text-slate-500">({rows[0].consignee?.name || rows[0].sender?.name || "—"})</span>
                </button>
              ) : null}
            </div>
            <div className="p-6 space-y-4">
              <Section label="Order">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Country *</label>
                  <div className="grid grid-cols-2 gap-2" data-testid="trip-country-toggle">
                    {["GH", "TG"].map((c) => {
                      const info = countryInfo(c);
                      const active = form.country === c;
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setForm({ ...form, country: c })}
                          data-testid={`trip-country-${c}`}
                          className={`border rounded-md px-3 py-2 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                            active ? "border-ps-blue bg-sky-50 text-ps-deep" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <span className="text-base leading-none">{info.flag}</span>
                          <span>{info.name}</span>
                          <span className="text-[10px] font-mono-data text-slate-500 ml-auto">{info.currency}</span>
                        </button>
                      );
                    })}
                  </div>
                  {form.country === "TG" && (
                    <div className="mt-1 text-[10px] text-slate-500" data-testid="trip-tg-hint">
                      CMR will be numbered <span className="font-mono-data">PS-TG/…</span> and printed in French. No yard fuel allocation.
                    </div>
                  )}
                </div>

                {/* Waybill source toggle */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Waybill source *</label>
                  <div className="grid grid-cols-2 gap-2" data-testid="waybill-source-toggle">
                    {[
                      { v: "portside", label: "Portside CMR", hint: "We issue & print" },
                      { v: "client",   label: "Client waybill", hint: "Client's own document" },
                    ].map((o) => {
                      const active = (form.waybill_source || "portside") === o.v;
                      return (
                        <button
                          key={o.v}
                          type="button"
                          onClick={() => setForm({ ...form, waybill_source: o.v })}
                          data-testid={`waybill-source-${o.v}`}
                          className={`text-left border rounded-md px-3 py-2 text-sm transition-colors ${
                            active ? "border-ps-blue bg-sky-50 text-ps-deep" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <div className="font-semibold flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${active ? "bg-ps-blue" : "bg-slate-300"}`} />
                            {o.label}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5">{o.hint}</div>
                        </button>
                      );
                    })}
                  </div>
                  {form.waybill_source === "client" && (
                    <div className="mt-3 space-y-3 border border-amber-200 bg-amber-50/40 rounded-md p-3" data-testid="client-waybill-fields">
                      <F
                        label="Client waybill № *"
                        value={form.client_waybill_number}
                        onChange={(v) => setForm({ ...form, client_waybill_number: v })}
                        required
                        testid="client-waybill-number"
                        placeholder="e.g. DH/26-05-117 or ARKAS/2026/0445"
                      />
                      <F
                        label="Issued by (defaults to consignor)"
                        value={form.client_waybill_issuer}
                        onChange={(v) => setForm({ ...form, client_waybill_issuer: v })}
                        testid="client-waybill-issuer"
                        placeholder={form.sender?.name || "Client name"}
                      />
                      <div className="text-[10px] text-slate-600 leading-snug">
                        We will not print our own CMR for this trip. Please scan the client's signed waybill in the <strong>CMR scans</strong> card after delivery.
                      </div>
                    </div>
                  )}
                </div>

                <F label="Order date *" type="date" value={form.order_date} onChange={(v) => setForm({ ...form, order_date: v })} required testid="trip-order-date" />
              </Section>

              <Section label="1 · Sender (consignor)">
                <CustomerPicker
                  role="sender"
                  testid="trip-sender-picker"
                  customers={customers.filter((c) => c.role === "sender" || c.role === "both")}
                  current={form.sender.name}
                  onPick={(c) => pickCustomerAs("sender", c)}
                />
                <F label="Name *" value={form.sender.name} onChange={(v) => setForm({ ...form, sender: { ...form.sender, name: v } })} required testid="trip-sender-name" />
                <F label="Address" value={form.sender.address} onChange={(v) => setForm({ ...form, sender: { ...form.sender, address: v } })} testid="trip-sender-addr" />
                <F label="Contact" value={form.sender.contact} onChange={(v) => setForm({ ...form, sender: { ...form.sender, contact: v } })} testid="trip-sender-contact" />
              </Section>

              <Section label="2 · Consignee (Place of Delivery)">
                <CustomerPicker
                  role="consignee"
                  testid="trip-cons-picker"
                  customers={customers.filter((c) => c.role === "consignee" || c.role === "both")}
                  current={form.consignee.name}
                  onPick={(c) => pickCustomerAs("consignee", c)}
                />
                <F label="Name *" value={form.consignee.name} onChange={(v) => setForm({ ...form, consignee: { ...form.consignee, name: v } })} required testid="trip-cons-name" />
                <F label="Address" value={form.consignee.address} onChange={(v) => setForm({ ...form, consignee: { ...form.consignee, address: v } })} testid="trip-cons-addr" />
                <F label="Contact" value={form.consignee.contact} onChange={(v) => setForm({ ...form, consignee: { ...form.consignee, contact: v } })} testid="trip-cons-contact" />
              </Section>

              <Section label="Route">
                <div className="grid grid-cols-2 gap-3">
                  <F label="Origin (4) *" value={form.origin} onChange={(v) => setForm({ ...form, origin: v })} required testid="trip-origin" placeholder="Tema Free Zone" />
                  <F label="Destination (3) *" value={form.destination} onChange={(v) => setForm({ ...form, destination: v })} required testid="trip-destination" placeholder="Kumasi" />
                </div>
              </Section>

              <Section label="Cargo">
                <F label="Description *" value={form.cargo_description} onChange={(v) => setForm({ ...form, cargo_description: v })} required testid="trip-cargo" placeholder="700 bags poultry feed pellets" />
                <div className="grid grid-cols-3 gap-3">
                  <F label="Packages" type="number" value={form.packages} onChange={(v) => setForm({ ...form, packages: v })} testid="trip-packages" />
                  <F label="Packing" value={form.packing} onChange={(v) => setForm({ ...form, packing: v })} testid="trip-packing" placeholder="50 kg bags" />
                  <F label="HS code" value={form.hs_code} onChange={(v) => setForm({ ...form, hs_code: v })} testid="trip-hs" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Gross weight (kg)" type="number" value={form.weight_kg} onChange={(v) => setForm({ ...form, weight_kg: v })} testid="trip-weight" />
                  <F label="Volume (m³)" type="number" step="0.01" value={form.volume_m3} onChange={(v) => setForm({ ...form, volume_m3: v })} testid="trip-volume" />
                </div>
                <F label="Special instructions" value={form.special_instructions} onChange={(v) => setForm({ ...form, special_instructions: v })} testid="trip-special" placeholder="Keep dry; deliver before 17:00" />
              </Section>

              <Section label="Carrier & charges">
                <div className="grid grid-cols-2 gap-3">
                  <F label="Truck" value={form.vehicle_number} onChange={(v) => setForm({ ...form, vehicle_number: v })} testid="trip-truck" placeholder="GT 9159" />
                  <F label="Driver" value={form.driver_name} onChange={(v) => setForm({ ...form, driver_name: v })} testid="trip-driver" placeholder="BASHIRU" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Charges paid by</label>
                    <select value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} data-testid="trip-payterms"
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      <option>Sender</option><option>Consignee</option><option>Prepaid</option>
                    </select>
                  </div>
                  <F label={`Agreed charge (${countryInfo(form.country || defaultCountry).currency})`} type="number" step="0.01" value={form.agreed_charge_ghs} onChange={(v) => setForm({ ...form, agreed_charge_ghs: v })} testid="trip-charge" />
                  <F label="Fuel needed (litres)" type="number" step="1" min="0" value={form.fuel_needed_l} onChange={(v) => setForm({ ...form, fuel_needed_l: v })} testid="trip-fuel-needed" placeholder="e.g. 250" />
                </div>
              </Section>

              {err && <div className="border border-red-300 bg-red-50 text-ps-red text-sm px-3 py-2 rounded-md">{err}</div>}
            </div>
            <div className="p-6 border-t border-slate-200 flex gap-3 sticky bottom-0 bg-white">
              <button type="button" onClick={() => setOpen(false)} className="flex-1 border border-slate-300 rounded-md py-2.5 text-sm hover:bg-slate-100">Cancel</button>
              <button type="submit" disabled={saving} data-testid="trip-submit" className="flex-1 bg-ps-blue text-white py-2.5 rounded-md text-sm font-semibold hover:bg-ps-deep transition-colors disabled:opacity-50">
                {saving ? "Creating…" : "Open order"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div className="border border-slate-200 rounded-md p-3 space-y-3 bg-slate-50/50">
      <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">{label}</div>
      {children}
    </div>
  );
}

function F({ label, testid, onChange, type = "text", ...props }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{label}</label>
      <input
        type={type}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
        {...props}
      />
    </div>
  );
}

// --- Smart customer picker: combobox with type-ahead, picks from the address book ---
function CustomerPicker({ role, customers, current, onPick, testid }) {
  const [q, setQ] = useState("");
  const [openList, setOpenList] = useState(false);
  const list = (q ? customers.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())) : customers).slice(0, 12);

  if (!customers || customers.length === 0) {
    return null; // empty address book → just show the manual fields
  }

  return (
    <div className="relative mb-2" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">
        <span>Pick from address book</span>
        {current && <span className="text-slate-400 normal-case tracking-normal">· current: <span className="font-mono-data text-slate-700">{current}</span></span>}
      </div>
      <div className="relative">
        <input
          value={q}
          onFocus={() => setOpenList(true)}
          onBlur={() => setTimeout(() => setOpenList(false), 150)}
          onChange={(e) => { setQ(e.target.value); setOpenList(true); }}
          placeholder={`Search ${role === "sender" ? "senders" : "consignees"}…`}
          data-testid={`${testid}-input`}
          className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-slate-50 focus:bg-white"
        />
        {openList && list.length > 0 && (
          <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto">
            {list.map((c) => (
              <button
                key={c.id}
                type="button"
                onMouseDown={(e) => {                 // mouseDown beats blur
                  e.preventDefault();
                  onPick(c);
                  setQ("");
                  setOpenList(false);
                }}
                data-testid={`${testid}-option-${c.id}`}
                className="w-full text-left px-3 py-2 hover:bg-sky-50 border-b border-slate-100 last:border-b-0"
              >
                <div className="font-semibold text-ps-deep text-sm flex items-center gap-2">
                  {c.name}
                  {role === "sender" && c.default_waybill_source === "client" && (
                    <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-bold bg-amber-50 text-amber-700 border border-amber-200">Own waybill</span>
                  )}
                  <span className="ml-auto text-[10px] text-slate-400 font-normal">used {c.usage_count}×</span>
                </div>
                {c.address && <div className="text-xs text-slate-500 truncate">{c.address}</div>}
                {(c.contact_name || c.contact_phone) && (
                  <div className="text-xs text-slate-400 font-mono-data truncate">{[c.contact_name, c.contact_phone].filter(Boolean).join(" · ")}</div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

